/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2019 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import ServiceRegistry from "bundle/util/ServiceRegistry";

const _isFilePreview = action => action.indexOf("preview") !== -1;

export default class DCInitService {
    constructor() {
        this._corePromise = undefined;
        this._dcApiReady = false;
        this._dcApiError = undefined;
        this._floodgateInitDone = false;
        this._checkForCore = this._checkForCore.bind(this);
        this._loggingService = ServiceRegistry.getService("LoggingService");
    }

    initialize(config) {
        this._config = config;
        this._checkForCore();
    }

    canExecute(action) {
        let intrvl;
        return new Promise((resolve => {
            if (_isFilePreview(action)) {
                resolve();
                return;
            }
            intrvl = setInterval(() => {
                if (this._dcApiReady) {
                    clearInterval(intrvl);
                    resolve();
                }
            }, 10);
        }));
    }

    _initFloodgate() {
        if (!this._config.floodgateEnabled || this._floodgateInitDone) {
            return;
        }
        this._floodgateInitDone = true;
        const helper = () => {
            const obj = {
                imsClientId: this._config.floodgateApiKey,
                floodgateUri: this._config.floodgateUri,
                useAnonymousUUID: this._config.floodgateUseAnonymousUUID,
            };
            this._corePromise.then(core => core.providers.floodgate(obj));
        };
        // do it async
        setTimeout(helper.bind(this), 2);
    }

    _checkForCore() {
        const sdk = window.adobe_dc_sdk;
        this._loggingService.logExternal("Waiting for DC core.");
        if (sdk && sdk.util) {
            this._loggingService.logExternal("DC core process started.");
            const self = this;
            sdk.launchOptions.sdkBase = "/view-sdk/__VERSION__/";
            sdk.launchOptions.app_uri = `${ sdk.launchOptions.sdkBase }AdobeDCViewApp.js`;
            self._dcExtraPromise = sdk.dom.loadScript(sdk.launchOptions.dc_extras_js_uri);
            self._dcMobxPromise = sdk.dom.loadScript(sdk.launchOptions.dc_mobx_js_uri);
            self._corePromise = sdk.appLauncher.corePromise;
            sdk.launchOptions.loadExtrasPromise = self._dcExtraPromise;
            sdk.dom.setEnvironment(sdk.launchOptions.env).then(() => {
                self._loggingService.logExternal("DC core process env ready.");
                const loggerDefer = new sdk.util.Deferred();
                self._loggingService.initDCLogging(self._corePromise, loggerDefer);
                self._corePromise.then(core => {
                    self._dcMobxPromise.then(() => {
                        sdk["dc-mobx"](sdk.aliases["dc-mobx"]);
                    });
                    core.envUtil.setEnvVars({
                        server_env: sdk.launchOptions.env,
                        ims_client_id: self._config.floodgateApiKey,
                        floodgate_uri: self._config.floodgateUri,
                    });
                    ServiceRegistry.getService("CMPToolService").initialize(this._config);
                    ServiceRegistry.getService("CMPToolService").ready().then(() => {
                        self._initMarketingTech();
                    });
                    self._loggingService.logExternal("Discovery setup - start.");
                    core.providers.discovery(
                        null,
                        window.adobe_dc_sdk.cache.VERSIONS,
                    ).then(discovery => {
                        self._loggingService.logExternal("Discovery setup - done.");
                        self._loggingService.logExternal("Rendition setup - start.");
                        const sTime = new Date().getTime();
                        core.providers.rendition(null, {
                            enableExternal: true,
                            useReview: false,
                            useDCAPI: false,
                            useAJSFullBuild: false,
                            useAJSRichBuild: true,
                        }).then(() => {
                            const rTime = Math.ceil(new Date().getTime() - sTime);
                            self._loggingService.logExternal("Rendition setup - done.", rTime);
                        });
                        self._loggingService.logExternal("Preview preload - start.");
                        discovery.loadDropinClass("preview").then(() => {
                            const pTime = Math.ceil(new Date().getTime() - sTime);
                            self._loggingService.logExternal("Preview preload - done.", pTime);
                        });
                    });
                    self._loggingService.logExternal("DC core part 1 is ready.");
                    self._initFloodgate();
                    self._dcExtraPromise.then(() => {
                        sdk["dc-extras"](sdk.aliases["dc-extras"]);
                        self._loggingService.logExternal("DC core part 2 is ready.");
                        self._loggingService.logExternal("React app loading - start.");
                        const asTime = new Date().getTime();
                        sdk.dom.loadScript(sdk.launchOptions.app_uri).then(() => {
                            const aeTime = Math.ceil(new Date().getTime() - asTime);
                            self._loggingService.logExternal("React app loading - done.", aeTime);
                            setTimeout(() => {
                                loggerDefer.resolve();
                            }, 1000);
                        });
                    });
                });
            });
        } else {
            setTimeout(this._checkForCore, 5);
        }
    }

    _isEnterpriseAccessibleDomain = () => (window.location.hostname.includes("documentservices")
            || window.location.hostname.includes("acrobatservices"));

    _getAnalyticsAccountsForEnterpriseAccessibleDomain = () => (this._config.serverEnv === "prod"
        ? "adbdcwebprod,adbadobenonacdcprod,adbadobeprototype" : "adbadobenonacdcqa,adbdcwebqa");

    _initMarketingTech() {
        const _initMarketingTechHelper = () => {
            if (!window.adobe_dc_sdk.gdpr_consent) return;

            // Convert our server environment name to the marketing one.
            const dev = "dev";
            const stage = "stage";
            const marketingEnv = {
                dev,
                stage,
                prod: "production",
            }[this._config.serverEnv] || "dev";
            window.marketingtech = {
                adobe: {
                    launch: {
                        property: "global",
                        environment: marketingEnv,
                    },
                    analytics: {
                        additionalAccounts: this._isEnterpriseAccessibleDomain()
                            ? this._getAnalyticsAccountsForEnterpriseAccessibleDomain() : "",
                    },
                },
                target: this._isEnterpriseAccessibleDomain(),
            };
            window.adobe_dc_sdk.dom.loadScript("https://www.adobe.com/marketingtech/main.min.js");
        };

        setTimeout(_initMarketingTechHelper, 20);
    }
}
